import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useContextMenu } from "../../../../contexts/RightClickMenuContext";
import { useTeamPresence } from "../../../../contexts/TeamPresenceContext";
import { useLayout } from "../../../../contexts/LayoutContext";
import {
  getThreads,
  renameThread,
  deleteThread,
} from "../../../../../api/chat";
import {
  getConversations,
  archiveConversation,
  getArchivedConversations,
  muteConversation,
  leaveGroup,
} from "../../../../../api/teamChat";
import { Archive, BellOff, Bell, LogOut, Pencil, Trash2 } from "lucide-react";

/* How long after a successful fetch to suppress duplicate calls.
   Prevents thrashing when multiple refresh events fire in quick
   succession (e.g. thread created + sidebar:refresh-threads + company
   change all within one render cycle).                                  */
const THREAD_FETCH_COOLDOWN_MS = 500;

/**
 * Build a deduped fetcher.
 * - If a fetch is in flight, subsequent calls return the same promise.
 * - If a fetch completed < COOLDOWN ms ago, it's skipped entirely.
 */
const makeDedupedFetcher = (fetchFn) => {
  const state = { inFlight: null, lastCompletedAt: 0 };
  return () => {
    if (state.inFlight) return state.inFlight;
    if (Date.now() - state.lastCompletedAt < THREAD_FETCH_COOLDOWN_MS) {
      return Promise.resolve();
    }
    state.inFlight = fetchFn().finally(() => {
      state.lastCompletedAt = Date.now();
      state.inFlight = null;
    });
    return state.inFlight;
  };
};

const useChatSidebarData = () => {
  const { currentAppPage } = useLayout();
  const navigate = useNavigate();
  const {
    onlineUsers,
    unreadCounts,
    mutedConversations,
    updateMutedConversation,
  } = useTeamPresence();

  const [threadsByType, setThreadsByType] = useState({
    private: [],
    public: [],
  });
  const [teamConversations, setTeamConversations] = useState({
    channels: [],
    groups: [],
    dms: [],
  });
  const [archivedIds, setArchivedIds] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [currentTeamId, setCurrentTeamId] = useState(null);
  const [expandedDirs, setExpandedDirs] = useState({
    private: true,
    public: false,
    team: false,
  });
  const [expandedTeamSubs, setExpandedTeamSubs] = useState({
    groups: true,
    dms: true,
    archived: false,
  });
  const [threadRefreshKey, setThreadRefreshKey] = useState(0);

  /* ── Data loading (deduped) ──
     The raw fetchers below are wrapped with makeDedupedFetcher once per
     component lifecycle via useRef, so every caller shares the same
     in-flight/cooldown state.                                            */
  const loadThreadsImpl = useCallback(async () => {
    try {
      const [privRes, pubRes] = await Promise.all([
        getThreads(0, "private"),
        getThreads(0, "public"),
      ]);
      setThreadsByType({
        private: privRes?.data?.threads || [],
        public: pubRes?.data?.threads || [],
      });
    } catch (e) {
      console.error("Error loading chat threads:", e);
      setThreadsByType({ private: [], public: [] });
    }
    setThreadRefreshKey((prev) => prev + 1);
  }, []);

  const loadTeamConversationsImpl = useCallback(async () => {
    try {
      const res = await getConversations();
      const data = res?.data || {};
      setTeamConversations({
        channels: data.channels || [],
        groups: data.groups || [],
        dms: data.dms || [],
      });
    } catch (e) {
      console.error("Error loading team conversations:", e);
      setTeamConversations({ channels: [], groups: [], dms: [] });
    }
  }, []);

  const loadThreadsRef = useRef(null);
  const loadTeamConversationsRef = useRef(null);
  if (!loadThreadsRef.current) {
    loadThreadsRef.current = makeDedupedFetcher(loadThreadsImpl);
  }
  if (!loadTeamConversationsRef.current) {
    loadTeamConversationsRef.current = makeDedupedFetcher(
      loadTeamConversationsImpl,
    );
  }

  // Keep the ref pointing at the newest deduped fn if loadThreadsImpl
  // ever changes identity. (Currently it's stable — empty deps — so this
  // is a no-op; kept for safety if deps are added later.)
  useEffect(() => {
    loadThreadsRef.current = makeDedupedFetcher(loadThreadsImpl);
  }, [loadThreadsImpl]);
  useEffect(() => {
    loadTeamConversationsRef.current = makeDedupedFetcher(
      loadTeamConversationsImpl,
    );
  }, [loadTeamConversationsImpl]);

  const loadThreads = useCallback(() => loadThreadsRef.current(), []);
  const loadTeamConversations = useCallback(
    () => loadTeamConversationsRef.current(),
    [],
  );

  /* ── Initial load ── */
  useEffect(() => {
    loadThreads();
    loadTeamConversations();
  }, [loadThreads, loadTeamConversations]);

  useEffect(() => {
    getArchivedConversations()
      .then((res) => setArchivedIds(res?.data?.archived_conversations || []))
      .catch((e) => console.error("Failed to load archived:", e));
  }, []);

  /* ── Reset on page change ── */
  useEffect(() => {
    if (currentAppPage !== "/chat") {
      setCurrentThreadId(null);
      setCurrentTeamId(null);
    }
  }, [currentAppPage]);

  /* ── Clear active state when another section becomes active ── */
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section && e.detail.section !== "chat") {
        setCurrentThreadId(null);
        setCurrentTeamId(null);
      }
    };
    window.addEventListener("sidebar:active-section-changed", handler);
    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, []);

  /* ── Refresh listeners ──
     Both of these go through the deduped fetchers, so redundant
     cross-firing (MainChat → sidebar, ensureThreadExists → sidebar,
     prefetch on hover, etc.) collapses to at most one network call per
     cooldown window.                                                     */
  useEffect(() => {
    const handler = () => {
      loadThreads();
      loadTeamConversations();
    };
    window.addEventListener("sidebar:refresh-threads", handler);
    return () => window.removeEventListener("sidebar:refresh-threads", handler);
  }, [loadThreads, loadTeamConversations]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.threadId !== undefined)
        setCurrentThreadId(e.detail.threadId);
      if (e.detail?.teamId !== undefined) setCurrentTeamId(e.detail.teamId);
    };
    window.addEventListener("sidebar:select-thread", handler);
    return () => window.removeEventListener("sidebar:select-thread", handler);
  }, []);

  useEffect(() => {
    const handler = () => loadTeamConversations();
    window.addEventListener("sidebar:refresh-team-conversations", handler);
    return () =>
      window.removeEventListener("sidebar:refresh-team-conversations", handler);
  }, [loadTeamConversations]);

  /* ── Right-click menu for team conversations ── */
  useContextMenu("team-conversation", [
    {
      label: "Mute",
      icon: BellOff,
      action: async (payload) => {
        if (!payload?.team_id) return;
        const isMuted = mutedConversations.includes(payload.team_id);
        await muteConversation(payload.team_id, !isMuted);
        updateMutedConversation(payload.team_id, !isMuted);
      },
      getLabel: (payload) =>
        mutedConversations.includes(payload?.team_id) ? "Unmute" : "Mute",
      getIcon: (payload) =>
        mutedConversations.includes(payload?.team_id) ? Bell : BellOff,
    },
    {
      label: "Archive",
      icon: Archive,
      action: async (payload) => {
        if (!payload?.team_id) return;
        const isArchived = archivedIds.includes(payload.team_id);
        await archiveConversation(payload.team_id, !isArchived);
        setArchivedIds((prev) =>
          isArchived
            ? prev.filter((id) => id !== payload.team_id)
            : [...prev, payload.team_id],
        );
      },
      getLabel: (payload) =>
        archivedIds.includes(payload?.team_id) ? "Unarchive" : "Archive",
    },
    {
      label: "Leave Group",
      icon: LogOut,
      variant: "danger",
      action: async (payload) => {
        if (!payload?.team_id) return;
        try {
          await leaveGroup(payload.team_id);
          loadTeamConversations();
          if (currentTeamId === payload.team_id) {
            setCurrentTeamId(null);
          }
        } catch (e) {
          console.error("Failed to leave group:", e);
        }
      },
      hidden: (payload) =>
        payload?.type === "channel" || payload?.type === "dm",
    },
  ]);

  /* ── Right-click menu for private/public threads ── */
  useContextMenu("chat-thread", [
    {
      label: "Rename",
      icon: Pencil,
      action: (payload) => {
        if (!payload?.thread_id) return;
        window.dispatchEvent(
          new CustomEvent("chat-thread:start-rename", {
            detail: { threadId: payload.thread_id },
          }),
        );
      },
    },
    {
      label: "Delete",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        if (!payload?.thread_id) return;
        window.dispatchEvent(
          new CustomEvent("chat-thread:start-delete", {
            detail: { threadId: payload.thread_id },
          }),
        );
      },
    },
  ]);

  /* ── Mapped data ── */
  const mappedByType = useMemo(
    () => ({
      private: threadsByType.private.map((t) => ({
        id: t.thread_id,
        label: t.title || "Thread",
        thread_type: t.thread_type || "private",
      })),
      public: threadsByType.public.map((t) => ({
        id: t.thread_id,
        label: t.title || "Thread",
        thread_type: "public",
      })),
    }),
    [threadsByType],
  );

  const allTeamMapped = useMemo(() => {
    const items = [];
    for (const ch of teamConversations.channels) {
      items.push({
        id: ch.team_id,
        label: ch.name || "Channel",
        thread_type: "team",
        isTeam: true,
      });
    }
    for (const g of teamConversations.groups) {
      items.push({
        id: g.team_id,
        label: g.name || "Group",
        thread_type: "team",
        isTeam: true,
      });
    }
    for (const dm of teamConversations.dms) {
      items.push({
        id: dm.team_id,
        label: dm.dm_display_name || "DM",
        thread_type: "team",
        isTeam: true,
      });
    }
    return items;
  }, [teamConversations]);

  const allMapped = useMemo(
    () => [...mappedByType.private, ...mappedByType.public, ...allTeamMapped],
    [mappedByType, allTeamMapped],
  );

  /* ── Breadcrumb ── */
  const emitChatBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/chat") return;
    const activeId = currentTeamId || currentThreadId;
    const activeThread = allMapped.find((t) => t.id === activeId);
    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/chat",
          subLabel: activeThread?.label || null,
          subIcon: null,
          hasAdd: true,
          addKind: "chat",
          addLabel: null,
        },
      }),
    );
  }, [currentThreadId, currentTeamId, allMapped, currentAppPage]);

  useEffect(() => {
    emitChatBreadcrumb();
  }, [emitChatBreadcrumb]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/chat") emitChatBreadcrumb();
    };
    window.addEventListener("breadcrumb:request", handler);
    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitChatBreadcrumb]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/chat") {
        setCurrentThreadId(null);
        setCurrentTeamId(null);
        window.dispatchEvent(
          new CustomEvent("sidebar:chat-thread-selected", {
            detail: { threadId: null, thread_type: "private" },
          }),
        );
        window.dispatchEvent(
          new CustomEvent("sidebar:active-section-changed", {
            detail: { section: "chat" },
          }),
        );
        if (currentAppPage !== "/chat") navigate("/chat");
      }
    };
    window.addEventListener("breadcrumb:add", handler);
    return () => window.removeEventListener("breadcrumb:add", handler);
  }, [currentAppPage, navigate]);

  /* ── Selection handlers ── */
  const handleSelect = useCallback(
    (threadId, thread_type = "private") => {
      setCurrentThreadId(threadId);
      setCurrentTeamId(null);
      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "chat" },
        }),
      );

      if (currentAppPage !== "/chat") {
        // MainChat isn't mounted yet — leave the selection on a global
        // so its mount-only effect can read it on first render.
        window.__pendingChatSelection = { threadId, thread_type };
        navigate("/chat");
      } else {
        window.dispatchEvent(
          new CustomEvent("sidebar:chat-thread-selected", {
            detail: { threadId, thread_type },
          }),
        );
      }
    },
    [currentAppPage, navigate],
  );

  const handleSelectTeam = useCallback(
    (teamId) => {
      setCurrentTeamId(teamId);
      setCurrentThreadId(null);
      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "chat" },
        }),
      );

      if (currentAppPage !== "/chat") {
        // MainChat isn't mounted yet — leave the selection on the global
        // so its mount-only effect can read it on first render.
        window.__pendingChatSelection = {
          threadId: null,
          thread_type: "team",
          team_id: teamId,
        };
        navigate("/chat");
      } else {
        window.dispatchEvent(
          new CustomEvent("sidebar:chat-thread-selected", {
            detail: { threadId: null, thread_type: "team", team_id: teamId },
          }),
        );
      }
    },
    [currentAppPage, navigate],
  );

  const handleNew = useCallback(
    (thread_type = "private") => {
      setCurrentThreadId(null);
      setCurrentTeamId(null);
      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "chat" },
        }),
      );

      if (currentAppPage !== "/chat") {
        window.__pendingChatSelection = { threadId: null, thread_type };
        navigate("/chat");
      } else {
        window.dispatchEvent(
          new CustomEvent("sidebar:chat-thread-selected", {
            detail: { threadId: null, thread_type },
          }),
        );
      }
    },
    [currentAppPage, navigate],
  );

  const toggleDir = useCallback((key) => {
    setExpandedDirs((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleTeamSub = useCallback((key) => {
    setExpandedTeamSubs((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return {
    // presence
    onlineUsers,
    unreadCounts,
    mutedConversations,
    // state
    mappedByType,
    teamConversations,
    archivedIds,
    currentThreadId,
    currentTeamId,
    expandedDirs,
    expandedTeamSubs,
    threadRefreshKey,
    // actions
    handleSelect,
    handleSelectTeam,
    handleNew,
    toggleDir,
    toggleTeamSub,
    loadTeamConversations,
    loadThreads,
  };
};

export default useChatSidebarData;
